/* ============================================
   CotizacionVisor — la cotización, sin salir del panel

   Abría en otra pestaña, con el enlace público. Funcionaba, pero sacaba al
   usuario de su CRM para leer un documento que es de su CRM: volver costaba
   encontrar la pestaña, y el contexto de dónde estaba parado se perdía.

   Se muestra el MISMO documento que ve el cliente final —el preview público
   del PDF, en un iframe— y no una vista propia: dos renderizados del mismo
   documento terminan diciendo cosas distintas, y el que importa es el que
   recibe el cliente. El enlace a la pestaña aparte sigue ahí, para mandarlo.
   ============================================ */
import { useState } from 'react'
import { formatCurrency } from '@/lib/utils'
import type { Cotizacion } from '@/lib/types'
import { llevaPreciosBase } from '@/lib/markup'
import { IconArrowLeft, IconExternalLink, IconCopy, IconCheck, IconAlertCircle, IconTag } from '@/components/icons/Icons'
import { Link } from 'react-router-dom'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

interface CotizacionVisorProps {
  cotizacion: Cotizacion
  /** Nombre de la empresa, que la cotización trae solo como id. */
  empresa?: string
  onVolver: () => void
}

export default function CotizacionVisor({ cotizacion: c, empresa, onVolver }: CotizacionVisorProps) {
  const [copiado, setCopiado] = useState(false)

  const enlacePublico = `${window.location.origin}/ver/${c.public_token}`
  /*
    Dos documentos distintos detrás de la misma pantalla. Una cotización
    armada acá se renderiza desde sus datos; una SUBIDA es un PDF y sus
    `items` están vacíos, así que renderizarla daría una tabla sin filas y un
    total en cero. La marca decide cuál de las dos rutas se abre.
  */
  const src = c.documento_subido
    ? `${API_URL}/api/pdf/public/cotizacion/${c.public_token}/archivo`
    : `${API_URL}/api/pdf/preview/public/cotizacion/${c.public_token}`

  async function copiar() {
    try {
      await navigator.clipboard.writeText(enlacePublico)
      setCopiado(true)
      window.setTimeout(() => setCopiado(false), 1800)
    } catch {
      /* Sin portapapeles no se insiste: el enlace sigue a un clic de distancia. */
    }
  }

  const base = llevaPreciosBase(c)

  return (
    <div className="cot-visor">
      {base && (
        <div className="cot-base-aviso">
          <IconAlertCircle size={15} />
          Cotización base — no se la mandes a tu cliente todavía.
          <span>Lleva los precios de 1Conecta: ponle tu markup y el documento se actualiza.</span>
          <Link to={`/partner/cotizaciones/${c.id}/editar`} className="btn btn-sm btn-primary">
            <IconTag size={13} /> Poner precio
          </Link>
        </div>
      )}
      <div className="cot-visor__barra">
        <button type="button" className="btn btn-sm btn-secondary" onClick={onVolver}>
          <IconArrowLeft size={14} /> Cotizaciones
        </button>

        <span className="cot-visor__identidad">
          <strong>{c.numero}</strong>
          {empresa && <span className="cot-visor__empresa">{empresa}</span>}
        </span>

        {/* Una propuesta de alcance no lleva importes: mostrar un total que el
            documento oculta sería contradecirlo. */}
        {c.mostrar_precios !== false && (
          <span className="cot-visor__total">{formatCurrency(Number(c.total || 0), c.moneda)}</span>
        )}

        <div className="cot-visor__acciones">
          <button type="button" className="btn btn-sm btn-secondary" onClick={copiar}>
            {copiado ? <><IconCheck size={13} /> Copiado</> : <><IconCopy size={13} /> Copiar enlace</>}
          </button>
          <a
            className="btn btn-sm btn-secondary"
            href={enlacePublico}
            target="_blank"
            rel="noreferrer"
            title="Abrir como lo ve el cliente"
          >
            <IconExternalLink size={13} />
          </a>
        </div>
      </div>

      {/*
        El `sandbox` va solo en el preview HTML. Un PDF se abre con el visor
        del propio navegador, que corre como una extensión y necesita permisos
        que el sandbox le quita: con `sandbox` el marco queda EN BLANCO, y con
        `allow-scripts` también — comprobado las dos. Dárselos todos sería
        escribir un sandbox que no aísla nada.

        Quitarlo no abre nada acá: lo que se muestra es un documento PDF, cuyo
        visor es del navegador y no del archivo, servido desde nuestra propia
        API. El preview HTML sí lo conserva.
      */}
      <iframe
        className="cot-visor__marco"
        src={src}
        title={`Cotización ${c.numero}`}
        {...(c.documento_subido ? {} : { sandbox: 'allow-popups allow-popups-to-escape-sandbox' })}
      />
    </div>
  )
}
